
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { sendToSalesforce } from '@/utils/salesforce';
import { useToast } from '@/components/ui/use-toast';
import { useLocation } from 'react-router-dom';
import BookingFormPersonalInfo from './BookingFormPersonalInfo';
import BookingFormServiceSelection from './BookingFormServiceSelection';
import BookingFormMessage from './BookingFormMessage';
import { mapServiceParam, mapPlanParam } from './utils/bookingFormOptions';

interface BookingFormProps {
  serviceParam?: string | null;
  planParam?: string | null;
  isResidential?: boolean;
}

const BookingForm: React.FC<BookingFormProps> = ({ 
  serviceParam, 
  planParam,
  isResidential = false
}) => {
  const { toast } = useToast();
  const location = useLocation();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    employees: '',
    services: [] as string[],
    plan: '',
    message: '',
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [functionsAvailable, setFunctionsAvailable] = useState<boolean | null>(null);
  
  useEffect(() => {
    // Set the service or plan from URL parameters if they exist
    const serviceName = mapServiceParam(serviceParam);
    const planName = mapPlanParam(planParam);
    
    if (serviceName || planName) {
      setFormData(prev => ({ 
        ...prev, 
        services: serviceName ? [serviceName] : prev.services,
        plan: planName || prev.plan 
      }));
    }
    
    // Check if we can reach the Supabase function
    const checkFunctionAvailability = async () => {
      try {
        // Try to make a simple OPTIONS request to the function
        const testResponse = await fetch('https://xjlpuyrqxgxnyixqwfml.supabase.co/functions/v1/process-form', {
          method: 'OPTIONS',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        setFunctionsAvailable(testResponse.status >= 200 && testResponse.status < 300);
        console.log('Supabase function availability check result:', testResponse.status, testResponse.ok);
        
      } catch (error) {
        console.error('Error checking function availability:', error);
        setFunctionsAvailable(false);
      }
    };
    
    checkFunctionAvailability();
  }, [serviceParam, planParam]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (service: string, isChecked: boolean) => {
    setFormData(prev => {
      if (isChecked) {
        return { ...prev, services: [...prev.services, service] };
      } else {
        return { ...prev, services: prev.services.filter(s => s !== service) };
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Log the submission attempt
    console.log('Form submission attempt with data:', {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      services: formData.services
    });
    
    // Check if functions are available based on our previous tests
    if (functionsAvailable === false) {
      toast({
        title: "Configuration Error",
        description: "The form handler function is not available. This is a configuration issue that needs to be fixed by the site administrator.",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      console.log('Submitting booking form data:', formData);
      
      // Send form data for email submission
      const formDataForSubmission = {
        ...formData,
        // Combine first and last name
        name: `${formData.firstName} ${formData.lastName}`,
        // Join multiple services into a comma-separated string
        service: formData.services.join(", "),
        formType: 'Website Booking Request',
        source: window.location.href,
        timestamp: new Date().toISOString(),
      };
      
      console.log('Prepared form data for submission:', {
        name: formDataForSubmission.name,
        email: formDataForSubmission.email,
        service: formDataForSubmission.service,
        formType: formDataForSubmission.formType
      });
      
      // Set a timeout to catch long-running requests
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error("Request timed out after 15 seconds")), 15000);
      });
      
      // Race the actual request with the timeout
      const result = await Promise.race([
        sendToSalesforce(formDataForSubmission, 'booking'),
        timeoutPromise
      ]) as any;
      
      console.log('Booking form submission result:', result);
      
      if (result.warning) {
        toast({
          title: "Consultation request sent, but with warning",
          description: `We'll contact you shortly. Note: ${result.warning}`,
          variant: "default",
        });
      } else if (result.email && !result.email.sent) {
        toast({
          title: "Consultation request recorded",
          description: "Your request has been received, but there was an issue sending the email notification. Our team will still process your request.",
          variant: "default",
        });
      } else {
        toast({
          title: "Consultation request sent!",
          description: "We'll contact you shortly to confirm your appointment.",
        });
      }
      
      // Reset form (but keep any pre-selected service or plan)
      const services = formData.services;
      const plan = formData.plan;
      
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        employees: '',
        services,
        plan,
        message: '',
      });
      
    } catch (error) {
      console.error('Error submitting form:', error);
      
      // Extract the most useful error message
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      
      // Special handling for Supabase function 404 errors
      if (errorMessage.includes('404') || errorMessage.includes('not found')) {
        toast({
          title: "Supabase Function Error",
          description: "The form handler function is not available. This is a configuration issue that needs to be fixed by the site administrator.",
          variant: "destructive",
        });
      } else if (errorMessage.includes('timeout')) {
        toast({
          title: "Request Timeout",
          description: "The request took too long to complete. Your server may be overloaded or misconfigured.",
          variant: "destructive",
        });
      } else if (errorMessage.includes('environment variable')) {
        toast({
          title: "Server Configuration Error",
          description: "Missing required environment variables. Please check your Supabase environment settings.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: `There was a problem booking your consultation: ${errorMessage}`,
          variant: "destructive",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-6">
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-itmgmt-dark-blue">Personal Information</h3>
          <BookingFormPersonalInfo 
            formData={formData} 
            handleChange={handleChange} 
          />
        </div>
        
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-itmgmt-dark-blue">Service Selection</h3>
          <BookingFormServiceSelection 
            formData={formData} 
            handleServiceChange={handleServiceChange}
            handleChange={handleChange} 
            isResidential={isResidential}
          />
        </div>
      </div>
      
      <div className="pt-2">
        <BookingFormMessage 
          message={formData.message} 
          handleChange={handleChange} 
        />
      </div>
      
      {functionsAvailable === false && (
        <div className="bg-red-50 border border-red-200 p-4 rounded text-red-800">
          <div className="flex items-center font-semibold text-base mb-2">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 mr-2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
            Supabase Function Error
          </div>
          <p>The form handler function is not available. This is a configuration issue that needs to be fixed by the site administrator.</p>
        </div>
      )}
      
      <div className="pt-4">
        <Button 
          type="submit" 
          className="button-primary w-full sm:w-auto px-8 py-3"
          disabled={isSubmitting || functionsAvailable === false}
        >
          {isSubmitting ? 'Submitting...' : 'Book Your Consultation'}
        </Button>
      </div>
    </form>
  );
};

export { BookingForm };
export default BookingForm;
